import type { ReactNode } from 'react';
import { useAuthStore } from '@/stores/auth';

type PermissionMode = 'all' | 'any';

interface PermissionGuardProps {
  permission?: string | string[];
  mode?: PermissionMode;
  fallback?: ReactNode;
  children: ReactNode;
}

const normalizePermissions = (permission?: string | string[]): string[] =>
  (Array.isArray(permission) ? permission : permission ? [permission] : [])
    .map((item) => String(item || '').trim())
    .filter(Boolean);

export const hasPermissions = (
  currentPermissions: string[],
  permission?: string | string[],
  mode: PermissionMode = 'all',
) => {
  const normalizedCurrentPermissions = currentPermissions
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  const requiredPermissions = normalizePermissions(permission);

  if (!requiredPermissions.length) {
    return true;
  }

  if (!normalizedCurrentPermissions.length) {
    return false;
  }

  if (mode === 'any') {
    return requiredPermissions.some((item) =>
      normalizedCurrentPermissions.includes(item),
    );
  }

  return requiredPermissions.every((item) =>
    normalizedCurrentPermissions.includes(item),
  );
};

export const usePermission = (
  permission?: string | string[],
  mode: PermissionMode = 'all',
) => {
  const currentPermissions = useAuthStore((state) => state.permissions);
  return hasPermissions(currentPermissions, permission, mode);
};

const PermissionGuard = ({
  permission,
  mode = 'all',
  fallback = null,
  children,
}: PermissionGuardProps) => {
  const canRender = usePermission(permission, mode);

  if (!canRender) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default PermissionGuard;
